import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import { ClinicProvider } from "./contexts/ClinicContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { ViewType } from "./components/Scheduler";
import TaskPerformanceReviewPage from "./pages/TaskPerformanceReview";
import { DoctorGate } from "@/hooks/useDoctorOnlyMode";
import AdminReferringDentists from "./pages/AdminReferringDentists";
import MyPatients from "./pages/MyPatients";
import DailyList from "./pages/DailyList";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15000),
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AuthProvider>
        <ClinicProvider>
          <TooltipProvider>
            <ErrorBoundary>
              <Toaster />
              <Sonner />
              <Routes>
                <Route path="/login" element={<Login />} />
                
                {/* Redirect root to daily view */}
                <Route path="/" element={<Navigate to="/daily" replace />} />
                
                {/* Individual view routes with nested tab routes */}
                {/* Daily view routes */}
                <Route
                  path="/daily"
                  element={
                    <ProtectedRoute>
                      <Index view="daily" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/daily/:tab"
                  element={
                    <ProtectedRoute>
                      <Index view="daily" />
                    </ProtectedRoute>
                  }
                />
                
                {/* Weekly view routes */}
                <Route
                  path="/weekly"
                  element={
                    <ProtectedRoute>
                      <Index view="weekly" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/weekly/:tab"
                  element={
                    <ProtectedRoute>
                      <Index view="weekly" />
                    </ProtectedRoute>
                  }
                />
                
                {/* Monthly view routes */}
                <Route
                  path="/monthly"
                  element={
                    <ProtectedRoute>
                      <Index view="monthly" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/monthly/:tab"
                  element={
                    <ProtectedRoute>
                      <Index view="monthly" />
                    </ProtectedRoute>
                  }
                />
                
                {/* Report view routes */}
                <Route
                  path="/report"
                  element={
                    <ProtectedRoute>
                      <Index view="report" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report/:tab"
                  element={
                    <ProtectedRoute>
                      <Index view="report" />
                    </ProtectedRoute>
                  }
                />
                
                {/* Automation view routes */}
                <Route
                  path="/automation"
                  element={
                    <ProtectedRoute>
                      <Index view="automation" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/automation/:tab"
                  element={
                    <ProtectedRoute>
                      <Index view="automation" />
                    </ProtectedRoute>
                  }
                />

                {/* Tasks view route (no tabs) */}
                <Route
                  path="/tasks"
                  element={
                    <ProtectedRoute>
                      <Index view="tasks" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tasks/performance-review"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary>
                        <DoctorGate>
                          <TaskPerformanceReviewPage />
                        </DoctorGate>
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />

                {/* Patients routes */}
                <Route
                  path="/patients"
                  element={
                    <ProtectedRoute>
                      <Index view="patients" />
                    </ProtectedRoute>
                  }
                />
                {/*
                  Sub-route slugs (e.g. referring-dentists) must be declared
                  BEFORE /patients/:id so the param matcher doesn't swallow them.
                  PatientsView reads `:id`, recognizes known tab slugs, and
                  activates the matching tab instead of trying to load a case.
                */}
                <Route
                  path="/patients/referring-dentists"
                  element={
                    <ProtectedRoute>
                      <Index view="patients" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/patients/:id"
                  element={
                    <ProtectedRoute>
                      <Index view="patients" />
                    </ProtectedRoute>
                  }
                />

                {/* Doctor-private "My Patients" routes (backlog #23) */}
                <Route
                  path="/my-patients"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary>
                        <MyPatients />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-patients/:caseId"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary>
                        <MyPatients />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />

                {/* Daily appointment list (per-doctor WhatsApp list) */}
                <Route
                  path="/daily-list"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary>
                        <DailyList />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />

                {/* Inventory routes */}
                <Route
                  path="/inventory"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/inventory/room-requests" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/room-requests"
                  element={
                    <ProtectedRoute>
                      <Index view="inventory" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/warehouse"
                  element={
                    <ProtectedRoute>
                      <Index view="inventory" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/purchase-orders"
                  element={
                    <ProtectedRoute>
                      <Index view="inventory" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/catalog"
                  element={
                    <ProtectedRoute>
                      <Index view="inventory" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/sync"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/inventory/warehouse" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/ipad"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/inventory/room-requests" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory/:id"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/inventory/room-requests" replace />
                    </ProtectedRoute>
                  }
                />
                
                {/* Admin view routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <Index view="admin" />
                    </ProtectedRoute>
                  }
                />
                {/* Standalone admin pages must be declared BEFORE /admin/:tab */}
                <Route
                  path="/admin/referring-dentists"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary>
                        <AdminReferringDentists />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/:tab"
                  element={
                    <ProtectedRoute>
                      <Index view="admin" />
                    </ProtectedRoute>
                  }
                />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ErrorBoundary>
          </TooltipProvider>
        </ClinicProvider>
      </AuthProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
